import {
  EnvelopeIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  UserGroupIcon,
  UserIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MapPinIcon,
  MapIcon,
  ListBulletIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import { Organization } from "../../shared/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { PractitionerDirectory } from "../../shared/types";
import { apiGet } from "src/utils/api";
import { useSearchParams } from "../../utils/url";
import PractitionerMap from "./PractitionerMap";
import { useUserLocation } from "../../hooks/useGoogleMaps";
import { latLongToZipcode } from "src/utils/utils";
import Input from "../inputs/Input";
import SelectInput from "../inputs/SelectInput";
import Button from "../Button";

type PractitionerSearchProps = {
  organization: Organization;
  specialties: string[];
  onPractitionerSelect: (practitioner: PractitionerDirectory) => void;
  setHasSearched?: (hasSearched: boolean) => void;
  mapEnabled: boolean;
  googleMapsApiKey: string;
  initialZipCode?: string;
}

type FormErrors = {
  zipcode?: string;
  specialty?: string;
  radius?: string;
}

type MobileView = 'list' | 'map';

const PractitionerSearch = ({ organization, specialties, onPractitionerSelect, setHasSearched, mapEnabled, googleMapsApiKey, initialZipCode }: PractitionerSearchProps) => {
  const [ searchParams, setSearchParams ] = useSearchParams();
  const initialZip = searchParams.get('zipcode') || initialZipCode || '53593';
  const [ zipcode, setZipcode ] = useState(initialZip);
  const [ mapZipcode, setMapZipcode ] = useState(initialZip);
  const [ specialty, setSpecialty ] = useState(searchParams.get('specialty') || 'General Surgery');
  const [ name, setName ] = useState(searchParams.get('name') || '');
  const [ radius, setRadius ] = useState(searchParams.get('radius') || '20');
  const [ formErrors, setFormErrors ] = useState<FormErrors>({});
  const [ practitioners, setPractitioners ] = useState<PractitionerDirectory[]>([]);
  const [ loading, setLoading ] = useState(false);
  const [ searchPerformed, setSearchPerformed ] = useState(false);
  const [ expandedSpecialties, setExpandedSpecialties ] = useState<Set<string>>(new Set());
  const [ focusedPractitioner, setFocusedPractitioner ] = useState<PractitionerDirectory | null>(null);
  const [ mobileView, setMobileView ] = useState<MobileView>('list');
  const resultsRef = useRef<HTMLDivElement>(null);
  const { userLocation, isUserLocationLoading, requestGeolocation, userLocationError, permissionDenied } = useUserLocation();

  useEffect(() => {
    if (zipcode.length === 5) {
      setMapZipcode(zipcode);
    }
  }, [zipcode]);

  const updateZipcodeFromLocation = useCallback(async (lat: number, lng: number) => {
    const zip = await latLongToZipcode(lat, lng, organization.id);
    if (zip) {
      setZipcode(zip);
    }
  }, [ organization.id ]);

  useEffect(() => {
    requestGeolocation();
  }, [ requestGeolocation ]);

  useEffect(() => {
    if (!loading && searchPerformed && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ loading, searchPerformed, practitioners ]);

  useEffect(() => {
    if (userLocation) {
      updateZipcodeFromLocation(userLocation.lat, userLocation.lng);
    }
  }, [ userLocation, updateZipcodeFromLocation ]);

  const validateForm = () => {
    const errors: FormErrors = {
      zipcode: !zipcode.trim() ? 'Required' : undefined,
      specialty: !specialty.trim() ? 'Required' : undefined,
      radius: !radius.trim() ? 'Required' : isNaN(Number(radius)) ? 'Must be a number' : undefined
    };

    const filteredErrors = Object.fromEntries(
      Object.entries(errors).filter(([_, value]) => value !== undefined)
    );

    setFormErrors(filteredErrors);
    return Object.keys(filteredErrors).length === 0;
  };

  const handleSearch = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setSearchPerformed(true);
    setPractitioners([]);

    const params = new URLSearchParams();
    params.append('zipcode', zipcode);
    params.append('specialty', specialty);
    if (name) params.append('name', name);
    params.append('radius', radius);
    const url = `/api/${organization.id}/ehr/directory?${params.toString()}`;
    const response = await apiGet(url);
    const data = await response.json();
    if (!data.error) {
      setPractitioners(data);
      setSearchParams(params, { replace: true });
    } else {
      setPractitioners([]);
    }

    if (setHasSearched) {
      setHasSearched(true);
    }
    setLoading(false);
  };

  const searchForm = (
    <div className={mapEnabled
      ? 'p-4 space-y-4 lg:border-b border-gray-100'
      : 'grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-5'
    }>
      <div>
        <Input
          type="text"
          required={true}
          label="Zip Code"
          placeholder="Enter your zip code (e.g., 90210)"
          value={zipcode}
          onChange={(e) => setZipcode(e.target.value)}
          inputClassName="py-2"
          helperText="Enter your zip code (e.g., 90210)"
          error={formErrors.zipcode}
          icon={<MapPinIcon className="w-5 h-5 mr-2 text-gray-600" />}
        />
        {mapEnabled && (
          <button
            onClick={requestGeolocation}
            disabled={isUserLocationLoading}
            className="text-brand-600 hover:text-brand-700 text-sm font-medium mt-1 flex items-center gap-1 disabled:text-gray-400"
          >
            {!userLocationError && !permissionDenied && (
              <>
                <MapPinIcon className="w-4 h-4" />
                {isUserLocationLoading ? 'Getting location...' : 'Use Current Location'}
              </>
            )}
          </button>
        )}
      </div>
      <SelectInput
        required={true}
        label="Search Radius"
        placeholder="Select search radius"
        value={radius}
        onChange={(e) => setRadius(e.target.value)}
        options={[
          { value: '5', label: '5 miles' },
          { value: '10', label: '10 miles' },
          { value: '15', label: '15 miles' },
          { value: '20', label: '20 miles' },
          { value: '50', label: '50 miles' },
          { value: '100', label: '100 miles' },
        ]}
        inputClassName="py-2"
        error={formErrors.radius}
        helperText="How far are you willing to travel?"
        icon={<MapPinIcon className="w-5 h-5 mr-2 text-gray-600" />}
      />
      <SelectInput
        required={true}
        label="Medical Specialty"
        placeholder="Select your required medical specialty"
        value={specialty}
        onChange={(e) => setSpecialty(e.target.value)}
        options={specialties.sort((a, b) => a.localeCompare(b)).map((spec) => ({ value: spec, label: spec }))}
        error={formErrors.specialty}
        inputClassName="py-2"
        helperText="Choose your required medical specialty"
        icon={<UserGroupIcon className="w-5 h-5 mr-2 text-gray-600" />}
      />
      <Input
        required={false}
        type="text"
        label="Provider Name"
        placeholder="Enter a portion of the provider's name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        inputClassName="py-2"
        helperText="Search for a provider by name or part of their name"
        icon={<UserIcon className="w-5 h-5 mr-2 text-gray-600" />}
      />

      <Button
        text={loading ? "Searching..." : "Search Providers"}
        onClick={handleSearch}
        disabled={loading}
        icon={<MagnifyingGlassIcon className="w-5 h-5" />}
        className="w-full justify-center mt-2 sm:col-span-2"
      />
    </div>
  );


  const ResultsList = () => (
    <div ref={resultsRef} className="flex-1">
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-gray-600 text-sm">Loading providers...</span>
          </div>
        </div>
      )}

      {!loading && practitioners.length === 0 && searchPerformed && (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <UserGroupIcon className="w-12 h-12 text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm text-center">No providers found matching your criteria</p>
          <p className="text-gray-400 text-xs text-center mt-1">Try adjusting your search filters</p>
        </div>
      )}

      {!loading && practitioners.length === 0 && !searchPerformed && (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <MagnifyingGlassIcon className="w-12 h-12 text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm text-center">Enter your criteria and search</p>
          <p className="text-gray-400 text-xs text-center mt-1">Fill in the required fields above</p>
        </div>
      )}

      {!loading && practitioners.length > 0 && (
        <>
          <div className="py-3 sm:py-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-800 text-center">
              {practitioners.length} Provider{practitioners.length !== 1 ? 's' : ''} Found
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 text-center">
              Providers matching your search criteria
            </p>
          </div>

          <div className="space-y-3 px-3 pb-3 lg:space-y-0 lg:px-0 lg:pb-0 lg:divide-y lg:divide-gray-200">
            {practitioners.map((practitioner) => (
              <div
                key={practitioner.practitioner_role_id}
                className="p-4 bg-white rounded-xl lg:rounded-none shadow-sm lg:shadow-none border border-gray-100 lg:border-0 overflow-hidden active:scale-[0.99] lg:active:scale-100 transition-all lg:hover:bg-gray-50 lg:cursor-pointer"
                onClick={() => setFocusedPractitioner(practitioner)}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 lg:w-10 lg:h-10 bg-gradient-to-br from-brand-100 to-brand-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <UserIcon className="w-5 h-5 text-brand-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base lg:text-lg font-bold text-gray-900 lg:text-gray-800 truncate">{practitioner.name}</h3>
                    <p className="text-xs text-brand-600 font-medium truncate lg:hidden">
                      {practitioner.specialty[0] && (practitioner.specialty[0].charAt(0).toUpperCase() + practitioner.specialty[0].slice(1))}
                      {practitioner.specialty.length > 1 && ` +${practitioner.specialty.length - 1} more`}
                    </p>
                  </div>
                </div>

                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(practitioner.location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-2 text-xs lg:text-sm text-gray-600 lg:text-brand-600 hover:text-brand-600 lg:hover:text-brand-700 lg:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MapPinIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5 lg:hidden" />
                  <span className="line-clamp-2 lg:line-clamp-none">{practitioner.location}</span>
                </a>

                <div className="flex items-center gap-2 mt-1.5 lg:my-2 text-sm text-gray-600">
                  <PhoneIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <a
                    href={`tel:${practitioner.locationPhone}`}
                    className="hover:text-brand-600 text-xs lg:text-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {practitioner.locationPhone}
                  </a>
                </div>

                {practitioner.practitionerEmail && (
                  <div className="flex items-center gap-2 mt-1.5 lg:mb-2 text-sm text-gray-600">
                    <EnvelopeIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate text-xs lg:text-sm">{practitioner.practitionerEmail}</span>
                  </div>
                )}

                <div className={`mb-3 mt-3 ${practitioner.specialty.length <= 1 ? 'hidden lg:block' : ''}`}>
                  <div className="flex items-start gap-2">
                    <UserGroupIcon className="w-4 h-4 text-brand-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <span className={`text-xs lg:text-sm text-gray-600 lg:text-gray-700 ${!expandedSpecialties.has(practitioner.practitioner_role_id || '') ? 'line-clamp-1 lg:line-clamp-2' : ''}`}>
                        {practitioner.specialty.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}
                      </span>
                      {practitioner.specialty.length > 2 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedSpecialties(prev => {
                              const newSet = new Set(prev);
                              if (newSet.has(practitioner.practitioner_role_id || '')) {
                                newSet.delete(practitioner.practitioner_role_id || '');
                              } else {
                                newSet.add(practitioner.practitioner_role_id || '');
                              }
                              return newSet;
                            });
                          }}
                          className="flex items-center gap-1 text-xs mt-1 text-brand-600 hover:text-brand-700 font-medium"
                        >
                          {expandedSpecialties.has(practitioner.practitioner_role_id || '') ? (
                            <>Show less <ChevronUpIcon className="w-3 h-3" /></>
                          ) : (
                            <>Show all <ChevronDownIcon className="w-3 h-3" /></>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onPractitionerSelect(practitioner);
                  }}
                  className="w-full bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white font-medium py-2.5 lg:py-2 px-4 rounded-lg lg:rounded-md transition-colors duration-200 text-sm flex items-center justify-center gap-2"
                >
                  <CalendarDaysIcon className="w-4 h-4 lg:hidden" />
                  Schedule Appointment
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const MobileViewToggle = () => {
    return (
      <div className="lg:hidden bg-gray-50 border-b border-gray-200 px-4 py-2">
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setMobileView('list')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-sm font-medium transition-all ${
              mobileView === 'list'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500'
            }`}
          >
            <ListBulletIcon className="w-4 h-4" />
            List
          </button>
          <button
            onClick={() => setMobileView('map')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-sm font-medium transition-all ${
              mobileView === 'map'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500'
            }`}
          >
            <MapIcon className="w-4 h-4" />
            Map
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col lg:flex-row min-h-[calc(100vh-64px)] lg:h-[calc(100vh-64px)] bg-gray-50">
      <div className={`${mapEnabled ? 'lg:w-1/4 lg:flex-shrink-0' : 'lg:max-w-4xl lg:mx-auto lg:p-6'} bg-white lg:border-r border-gray-200 flex flex-col lg:overflow-y-auto`}>
        <div className="bg-gray-50 lg:bg-transparent border-b lg:border-0 border-gray-200 px-3 lg:px-0 pt-3 lg:pt-0 pb-1 lg:pb-0">
          <div className={!mapEnabled ? 'max-w-xl lg:max-w-none mx-auto lg:mx-0' : ''}>
            {searchForm}
          </div>
        </div>

        <MobileViewToggle />

        <div className={`flex-1 relative ${mobileView !== 'list' ? 'hidden lg:block' : ''}`}>
          <ResultsList />
        </div>

        {practitioners.length > 0 && mapEnabled && (
          <div className="hidden lg:block px-4 py-3 bg-gray-50 border-t border-gray-200 text-center">
            <span className="text-sm text-gray-600">
              {practitioners.length} provider{practitioners.length !== 1 ? 's' : ''} found
            </span>
          </div>
        )}
      </div>

      {mapEnabled && googleMapsApiKey && (
        <div className={`lg:flex-1 relative ${mobileView !== 'map' ? 'hidden lg:block' : 'h-[calc(100vh-64px-200px)] min-h-[400px] lg:h-auto'}`}>
          <PractitionerMap
            practitioners={practitioners}
            onPractitionerSelect={onPractitionerSelect}
            zipcode={mapZipcode}
            searchRadiusMiles={Number(radius)}
            focusedPractitioner={focusedPractitioner}
            setFocusedPractitioner={setFocusedPractitioner}
            googleMapsApiKey={googleMapsApiKey}
          />
        </div>
      )}
    </div>
  );
};

export default PractitionerSearch;
